import { observer } from 'mobx-react-lite';
import * as React from 'react';
import CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import ColorScheme from 'owa-color-utils/lib/ColorScheme';
import ComposeStyles from '../utils/ComposeStyles';
import createRecurrenceFromSelectedDays from 'owa-recurrence-utils/lib/createRecurrenceFromSelectedDays';
import getRecurrenceEndDate from 'owa-recurrence-utils/lib/getRecurrenceEndDate';
import getRecurrenceInterval from 'owa-recurrence-utils/lib/getRecurrenceInterval';
import getRecurrenceMenuItems from '../utils/recurrence/getRecurrenceMenuItems';
import getSelectedDaysFromRecurrence from 'owa-recurrence-utils/lib/getSelectedDaysFromRecurrence';
import getSelectedRecurrenceKey from 'owa-recurrence-utils/lib/getSelectedRecurrenceKey';
import getSelectedRecurrenceString from 'owa-recurrence-utils/lib/getSelectedRecurrenceString';
import isCustomRecurrenceType from 'owa-recurrence-utils/lib/isCustomRecurrenceType';
import RecurrenceChoiceComponent from 'owa-recurrence/lib/components/RecurrenceChoiceComponent';
import RecurrenceDaysComponent, {
    RecurrenceDaysColor,
} from 'owa-recurrence/lib/components/RecurrenceDaysComponent';
import RecurrenceInterval from 'owa-recurrence/lib/components/RecurrenceInterval';
import RecurrenceMenu from 'owa-recurrence/lib/components/RecurrenceMenu';
import RecurrenceSummary from 'owa-recurrence/lib/components/RecurrenceSummary';
import RecurrenceType from 'owa-service/lib/contract/RecurrenceType';
import { getPalette } from 'owa-theme';
import { RecurrenceConstants, RecurrenceTypes } from 'owa-recurrence-utils/lib/RecurrenceTypes';
import {
    isDailyRecurrence,
    isMonthlyRecurrence,
    isWeeklyRecurrence,
    isYearlyRecurrence,
} from 'owa-recurrence-utils/lib/checkRecurrenceTypes';
import { getUserTimeZone } from 'owa-session-store';
import getRecurrenceToRender from 'owa-calendar-meeting-forms-common-view/lib/utils/getRecurrenceToRender';

let styles = require('./RecurrenceComponent.scss');
let classNames = require('classnames/bind').bind(styles);

export interface RecurrenceProps extends React.HTMLProps<HTMLDivElement> {
    event: CalendarEvent;
    colorScheme: ColorScheme;
    cssVariablesStyle?: ComposeStyles;
    onRecurrenceChangedCallback: (recurrence: RecurrenceType) => void;
    onSizeUpdate?: () => void;
    isNewEvent: boolean;
}

export interface RecurrenceState {
    isCustomSelected: boolean;
}

export default observer(function RecurrenceComponent(props_0: RecurrenceProps) {
    const interval = React.useRef<number>();
    const container = React.useRef<HTMLDivElement>();
    const lastHeight = React.useRef<number>();
    const [isCustomSelected, setIsCustomSelected] = React.useState(false);
    const onRecurrenceChanged = (recurrenceSelection: RecurrenceTypes) => {
        let { onRecurrenceChangedCallback, event } = props_0;
        setIsCustomSelected(recurrenceSelection === RecurrenceConstants.Custom);
        onRecurrenceChangedCallback(
            getRecurrenceToRender(event.Recurrence, recurrenceSelection, event.Start)
        );
    };
    const getRecurrenceDaysColor = (): RecurrenceDaysColor => {
        const { colorScheme } = props_0;
        const themeSymbols = getPalette();
        return {
            checkedColor: colorScheme ? colorScheme.calPrimary : themeSymbols.themePrimary,
            checkedHoverColor: colorScheme ? colorScheme.calDarkAlt : themeSymbols.themeDarkAlt,
        };
    };
    const onDaySelectionChangedCallback = (selectedDays: string[]): void => {
        let { onRecurrenceChangedCallback, event } = props_0;
        let endDate = getRecurrenceEndDate(getUserTimeZone(), event.Recurrence);
        onRecurrenceChangedCallback(
            createRecurrenceFromSelectedDays(event.Start, selectedDays, interval.current, endDate)
        );
    };
    React.useEffect(() => {
        let newHeight = container.current.getBoundingClientRect().height;
        if (newHeight != lastHeight.current && props_0.onSizeUpdate) {
            props_0.onSizeUpdate();
        }
        lastHeight.current = newHeight;
    });
    const timeZoneId = getUserTimeZone();
    let { event, colorScheme, className, onRecurrenceChangedCallback, isNewEvent } = props_0;
    let recurrence = event.Recurrence;
    let currentRecurrenceString = getSelectedRecurrenceString(recurrence);
    let currentRecurrenceKey: string = getSelectedRecurrenceKey(
        recurrence,
        true /* includeCustom*/
    );
    let recurrenceMenuItems = getRecurrenceMenuItems(null, !isNewEvent /* isNeverDisabled */);
    // Show RecurrenceInterval component if recurrence is custom or User selected custom option.
    let isRecurrenceCustomType: boolean = isCustomRecurrenceType(recurrence) || isCustomSelected;
    interval.current = getRecurrenceInterval(recurrence);
    return (
        <div ref={container} className={classNames(styles.recurrenceControls, className)}>
            <div className={isRecurrenceCustomType ? null : styles.recurrenceMenu}>
                {isRecurrenceCustomType ? (
                    <RecurrenceInterval
                        recurrence={recurrence}
                        startDate={event.Start}
                        onRecurrenceChangedCallback={onRecurrenceChangedCallback}
                        colorScheme={colorScheme}
                        className={styles.recurrenceInterval}
                    />
                ) : (
                    <RecurrenceMenu
                        recurrenceMenuItems={recurrenceMenuItems}
                        currentRecurrenceString={currentRecurrenceString}
                        currentRecurrenceKey={currentRecurrenceKey}
                        onRecurrenceChangedCallback={onRecurrenceChanged}
                        colorScheme={colorScheme}
                    />
                )}
            </div>
            {((isDailyRecurrence(recurrence) && getRecurrenceInterval(recurrence) === 1) ||
                isWeeklyRecurrence(recurrence)) && (
                <div>
                    <RecurrenceDaysComponent
                        colors={getRecurrenceDaysColor()}
                        className={styles.recurrenceDaysControl}
                        selectedDays={getSelectedDaysFromRecurrence(recurrence)}
                        onDaySelectionChangedCallback={onDaySelectionChangedCallback}
                    />
                </div>
            )}
            {(isYearlyRecurrence(recurrence) || isMonthlyRecurrence(recurrence)) && (
                <div>
                    <RecurrenceChoiceComponent
                        start={event.Start}
                        timeZoneId={getUserTimeZone()}
                        recurrence={recurrence}
                        onSelectionChangedCallback={onRecurrenceChangedCallback}
                        className={styles.recurrenceChoiceControl}
                    />
                </div>
            )}
            {recurrence && (
                <RecurrenceSummary
                    colorScheme={props_0.colorScheme}
                    timeZoneId={timeZoneId}
                    event={event}
                    recurrence={recurrence}
                    onEndRecurrenceChangedCallback={onRecurrenceChangedCallback}
                    cssVariablesStyle={props_0.cssVariablesStyle}
                />
            )}
        </div>
    );
});
